/* Native forms remain the source of truth. */
(() => {
  'use strict';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  // Reveal on intersection rather than hiding content in CSS: content stays readable
  // if JavaScript, observers, or animation support is unavailable.
  const motionTargets = new WeakSet();
  const activeAnimations = new Map();
  const motionSelector = '.section-heading, .craving-tile, .bundle-card, .discovery-heading, .discovery-stage, .discovery-bottom, .story-heading, .story-image, .story-copy, .recipe-card, .footer-top, .page-intro, .story-page__intro, .product-card, .preview-category-card';
  const motionEnabled = () => !reducedMotion.matches && document.body.dataset.motion !== 'off';
  function finishMotion(element) {
    const animation = activeAnimations.get(element);
    if (animation) { animation.cancel(); activeAnimations.delete(element); }
  }
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const element = entry.target;
      revealObserver.unobserve(element);
      if (!motionEnabled() || !element.animate || element.getBoundingClientRect().top < 0 || element.contains(document.activeElement)) return;
      const siblings = Array.from(element.parentElement.children).filter(child => child.matches(motionSelector));
      const delay = Math.min(siblings.indexOf(element), 3) * 65;
      const animation = element.animate([
        { opacity: 0, transform: 'translateY(24px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 650, delay, easing: 'cubic-bezier(.2,.65,.3,1)', fill: 'backwards' });
      activeAnimations.set(element, animation);
      animation.onfinish = () => activeAnimations.delete(element);
    });
  }, { threshold: 0.08 }) : null;
  function initializeMotion(root) {
    if (!motionEnabled() || !revealObserver) return;
    root.querySelectorAll(motionSelector).forEach(element => {
      if (motionTargets.has(element)) return;
      motionTargets.add(element);
      revealObserver.observe(element);
    });
  }
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) {
      activeAnimations.forEach(animation => animation.cancel());
      activeAnimations.clear();
    } else { initializeMotion(document); }
  });
  document.addEventListener('focusin', event => {
    activeAnimations.forEach((animation, element) => {
      if (element.contains(event.target)) finishMotion(element);
    });
  });
  function initialize(root = document) {
    initializeMotion(root);
    root.querySelectorAll('[data-carousel]').forEach(carousel => {
      if (initialized.has(carousel)) return;
      initialized.add(carousel);
      const track = carousel.querySelector('[data-track]');
      const prev = carousel.querySelector('[data-prev]');
      const next = carousel.querySelector('[data-next]');
      const update = () => {
        prev.disabled = track.scrollLeft < 3;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 3;
      };
      const move = direction => {
        const item = track.firstElementChild;
        if (!item) return;
        const step = item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
        track.scrollBy({ left: direction * step, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      };
      prev.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('scroll', update, { passive: true });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(update);
        observer.observe(track);
        carousel._ngCleanup = () => observer.disconnect();
      }
      update();
    });
    root.querySelectorAll('[data-marquee-toggle]').forEach(button => {
      if (initialized.has(button)) return;
      initialized.add(button);
      button.addEventListener('click', () => {
        const paused = button.closest('[data-marquee]').classList.toggle('is-paused');
        button.setAttribute('aria-pressed', String(paused));
        button.querySelector('[data-pause]').hidden = paused;
        button.querySelector('[data-play]').hidden = !paused;
      });
    });
    root.querySelectorAll('[data-variant-select]').forEach(select => {
      if (initialized.has(select)) return;
      initialized.add(select);
      const initialButton = select.closest('.product-page').querySelector('[data-add-button]');
      initialButton.disabled = select.selectedOptions[0].dataset.available !== 'true';
      initialButton.textContent = initialButton.disabled ? initialButton.dataset.soldLabel : initialButton.dataset.availableLabel;
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const section = select.closest('.product-page');
        const button = section.querySelector('[data-add-button]');
        const price = section.querySelector('[data-product-price]');
        price.textContent = option.dataset.price;
        if (option.dataset.compare) {
          const compare = document.createElement('s'); compare.textContent = option.dataset.compare; price.append(' ', compare);
        }
        section.querySelector('[data-unit-price]').textContent = option.dataset.unit || '';
        button.disabled = option.dataset.available !== 'true';
        button.textContent = button.disabled ? button.dataset.soldLabel : button.dataset.availableLabel;
        const url = new URL(window.location.href); url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
        const gallery = section.querySelector('.product-gallery');
        const media = Array.from(gallery.children).find(item => item.id.endsWith('-' + option.dataset.media));
        if (media) {
          if (window.matchMedia('(max-width: 749px)').matches) {
            gallery.scrollTo({ left: media.offsetLeft - gallery.firstElementChild.offsetLeft, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
          } else { media.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'nearest' }); }
        }
      });
    });
    root.querySelectorAll('[data-nav-dropdown]').forEach(dropdown => {
      if (initialized.has(dropdown)) return;
      initialized.add(dropdown);
      dropdown.addEventListener('toggle', () => {
        if (!dropdown.open) return;
        dropdown.closest('nav').querySelectorAll('[data-nav-dropdown]').forEach(other => {
          if (other !== dropdown) other.open = false;
        });
      });
      dropdown.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.stopPropagation(); dropdown.open = false;
          dropdown.querySelector('summary').focus();
        }
      });
      dropdown.addEventListener('focusout', () => {
        requestAnimationFrame(() => { if (!dropdown.contains(document.activeElement)) dropdown.open = false; });
      });
    });
    root.querySelectorAll('.mobile-menu').forEach(menu => {
      if (initialized.has(menu)) return;
      initialized.add(menu);
      menu.addEventListener('keydown', event => {
        if (event.key === 'Escape') { menu.open = false; menu.querySelector('summary').focus(); }
      });
      menu.addEventListener('click', event => { if (event.target.closest('a')) menu.open = false; });
      menu.addEventListener('focusout', () => {
        requestAnimationFrame(() => { if (!menu.contains(document.activeElement)) menu.open = false; });
      });
    });
    root.querySelectorAll('[data-scroll-next]').forEach(link => {
      if (initialized.has(link)) return;
      initialized.add(link);
      link.addEventListener('click', event => {
        const next = link.closest('.shopify-section')?.nextElementSibling;
        if (next) { event.preventDefault(); next.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth' }); }
      });
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:select', event => {
    event.target.querySelectorAll(motionSelector).forEach(element => { revealObserver?.unobserve(element); finishMotion(element); });
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(motionSelector).forEach(element => { revealObserver?.unobserve(element); finishMotion(element); });
    event.target.querySelectorAll('[data-carousel]').forEach(carousel => carousel._ngCleanup?.());
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('[data-nav-dropdown][open]').forEach(dropdown => { if (!dropdown.contains(event.target)) dropdown.open = false; });
    document.querySelectorAll('.mobile-menu[open]').forEach(menu => { if (!menu.contains(event.target)) menu.open = false; });
  });
})();

document.querySelectorAll('[data-bundle-variant]').forEach((radio) => {
  radio.addEventListener('change', () => {
    const form = radio.closest('.product-form');
    const select = form.querySelector('[data-variant-select]');

    select.value = radio.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    form.querySelectorAll('.bundle-variant-card').forEach((card) => {
      card.classList.remove('is-selected');
    });

    radio.closest('.bundle-variant-card').classList.add('is-selected');
  });
});